import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';

// Import client logos
import ginos from '../assets/clients/ginos.png';
import pizzaville from '../assets/clients/pizzaville.png';
import pizza241 from '../assets/clients/241.png';
import bestbuy from '../assets/clients/bestbuybrand.png';
import dd from '../assets/clients/dd.png';
import acl from '../assets/clients/acl.png';
import hotline from '../assets/clients/hotline.png';
import asianparts from '../assets/clients/asianparts.png';
import gp2 from '../assets/clients/gp2.png';
import tsi from '../assets/clients/tsi.png';
import uap from '../assets/clients/uap.png';

interface Logo {
  name: string;
  id: number;
  img: string;
  url: string;
}

const allLogos: Logo[] = [
  { name: "Gino's Pizza", id: 1, img: ginos, url: "https://ginospizza.ca" },
  { name: "Pizzaville", id: 2, img: pizzaville, url: "https://pizzaville.ca" },
  { name: "241 Pizza", id: 3, img: pizza241, url: "https://241pizza.com" },
  { name: "German Parts", id: 4, img: gp2, url: "https://germanparts.ca" },
  { name: "Asian Parts", id: 5, img: asianparts, url: "https://asianparts.ca" },
  { name: "Pizza Hotline", id: 6, img: hotline, url: "https://pizzahotline.ca" },
  { name: "Double Double", id: 7, img: dd, url: "https://doubledouble.ca" },
  { name: "United Auto Parts", id: 8, img: uap, url: "https://www.uapinc.com/en-ca" },
  { name: "Altrom Auto-Camping", id: 9, img: acl, url: "https://autocamping.ca" },
  { name: "Sign Installers", id: 10, img: tsi, url: "https://thesigninstallers.com" },
  { name: "Best Buy Distributors", id: 11, img: bestbuy, url: "https://www.bestbuydistributors.ca" },
];

// Utility functions
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const distributeLogos = (allLogos: Logo[], columnCount: number): Logo[][] => {
  const shuffled = shuffleArray(allLogos);
  const columns: Logo[][] = Array.from({ length: columnCount }, () => []);

  shuffled.forEach((logo, index) => {
    columns[index % columnCount].push(logo);
  });

  const maxLength = Math.max(...columns.map((col) => col.length));
  columns.forEach((col) => {
    while (col.length < maxLength) {
      col.push(shuffled[Math.floor(Math.random() * shuffled.length)]);
    }
  });

  return columns;
};

// Styled Components
const CarouselSection = styled.section`
  padding: 4rem 0 5rem;
  background-color: transparent;
  position: relative;
  overflow: hidden;
  border-top: 1px solid rgba(230, 230, 240, 0.7);
  border-bottom: 1px solid rgba(230, 230, 240, 0.7);
  
  @media (max-width: 768px) {
    padding: 3rem 0;
    border-top: none;
  }
`;

const CarouselTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 0.75rem;
  color: #2a3b4c;
`;

const CarouselSubtitle = styled.p`
  font-size: 1.05rem;
  color: #546677;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  line-height: 1.6;
`;

const LogoGrid = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: clamp(0.5rem, 2.5vw, 3rem);
  padding: 0 clamp(1rem, 4vw, 4rem);
`;

/* Columns divide the section width evenly so the row reads as one full-bleed
   band rather than a cluster floating in the middle. */
const ColumnWrapper = styled(motion.div)`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  height: 80px;
  overflow: hidden;
  
  @media (min-width: 768px) {
    height: 100px;
  }
`;

const LogoSlot = styled(motion.div)`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoImage = styled.img`
  max-height: 60px;
  max-width: 80%;
  object-fit: contain;
  filter: grayscale(100%);
  opacity: 0.7;
  transition: filter 0.3s ease, opacity 0.3s ease;
  
  &:hover {
    filter: grayscale(0%);
    opacity: 1;
  }
  
  @media (min-width: 768px) {
    max-height: 70px;
  }
`;

// LogoColumn Component
interface LogoColumnProps {
  logos: Logo[];
  index: number;
  currentTime: number;
}

const LogoColumn: React.FC<LogoColumnProps> = React.memo(({ logos, index, currentTime }) => {
  const cycleInterval = 2500;
  const columnDelay = index * 300;
  const adjustedTime = (currentTime + columnDelay) % (cycleInterval * logos.length);
  const currentIndex = Math.floor(adjustedTime / cycleInterval);
  const currentLogo = useMemo(() => logos[currentIndex], [logos, currentIndex]);

  return (
    <ColumnWrapper
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: index * 0.1,
        duration: 0.5,
        ease: "easeOut",
      }}
    >
      <AnimatePresence mode="wait">
        <LogoSlot
          key={`${currentLogo.id}-${currentIndex}`}
          initial={{ y: "15%", opacity: 0, filter: "blur(8px)" }}
          animate={{
            y: "0%",
            opacity: 1,
            filter: "blur(0px)",
          }}
          exit={{
            y: "-20%",
            opacity: 0,
            filter: "blur(6px)",
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 25,
            mass: 1,
          }}
        >
          <a href={currentLogo.url} target="_blank" rel="noopener noreferrer">
            <LogoImage
              src={currentLogo.img}
              alt={`${currentLogo.name} - UPTREND client`}
              loading="lazy"
            />
          </a>
        </LogoSlot>
      </AnimatePresence>
    </ColumnWrapper>
  );
});

// Main LogoCarousel Component
interface LogoCarouselProps {
  columnCount?: number;
  title?: string;
  subtitle?: string;
  showTitle?: boolean;
}

const LogoCarousel: React.FC<LogoCarouselProps> = ({
  columnCount = 5,
  title = "Trusted by Leading Businesses",
  subtitle = "We've partnered with forward-thinking companies across industries to deliver exceptional digital solutions",
  showTitle = true
}) => {
  const [logoSets, setLogoSets] = useState<Logo[][]>([]);
  const [currentTime, setCurrentTime] = useState(0);

  const updateTime = useCallback(() => {
    setCurrentTime((prevTime) => prevTime + 100);
  }, []);

  useEffect(() => {
    const intervalId = setInterval(updateTime, 100);
    return () => clearInterval(intervalId);
  }, [updateTime]);

  useEffect(() => {
    const distributedLogos = distributeLogos(allLogos, columnCount);
    setLogoSets(distributedLogos);
  }, [columnCount]);

  // Responsive column count
  const [responsiveColumnCount, setResponsiveColumnCount] = useState(columnCount);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 480) {
        setResponsiveColumnCount(2);
      } else if (window.innerWidth < 768) {
        setResponsiveColumnCount(3);
      } else if (window.innerWidth < 1024) {
        setResponsiveColumnCount(4);
      } else {
        setResponsiveColumnCount(columnCount);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [columnCount]);

  useEffect(() => {
    const distributedLogos = distributeLogos(allLogos, responsiveColumnCount);
    setLogoSets(distributedLogos);
  }, [responsiveColumnCount]);

  return (
    <CarouselSection>
      {showTitle && (
        <div className="container font--jakarta">
          <CarouselTitle>{title}</CarouselTitle>
          <CarouselSubtitle>{subtitle}</CarouselSubtitle>
        </div>
      )}

      <LogoGrid>
        {logoSets.map((logos, index) => (
          <LogoColumn
            key={index}
            logos={logos}
            index={index}
            currentTime={currentTime}
          />
        ))}
      </LogoGrid>
    </CarouselSection>
  );
};

export default LogoCarousel;

